/**
 * Keyboard movement with local prediction.
 *
 * The local avatar moves immediately against the same collision code the
 * server runs, so walking feels instant; the server only ever corrects it.
 * Movement is suppressed while a text field has focus so typing "was" does not
 * walk you into a wall.
 */

import {
  INPUT_HZ,
  WALK_SPEED,
  resolveMove,
  type Facing,
} from '@virtual-office/shared';

import type { Connection } from './net.js';
import type { Store } from './state.js';

const KEY_TO_AXIS: Record<string, { dx: number; dy: number; facing: Facing }> = {
  KeyW: { dx: 0, dy: -1, facing: 'up' },
  ArrowUp: { dx: 0, dy: -1, facing: 'up' },
  KeyS: { dx: 0, dy: 1, facing: 'down' },
  ArrowDown: { dx: 0, dy: 1, facing: 'down' },
  KeyA: { dx: -1, dy: 0, facing: 'left' },
  ArrowLeft: { dx: -1, dy: 0, facing: 'left' },
  KeyD: { dx: 1, dy: 0, facing: 'right' },
  ArrowRight: { dx: 1, dy: 0, facing: 'right' },
};

/**
 * True when keystrokes belong to the interface rather than the world.
 *
 * Covers both a focused form field and any open modal overlay — walking the
 * character around behind a dialog you are reading is disconcerting, and you
 * arrive somewhere unexpected when you close it.
 */
export function isTyping(): boolean {
  if (document.querySelector('.overlay:not([hidden])')) return true;

  const active = document.activeElement;
  if (!active) return false;
  const tag = active.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    (active as HTMLElement).isContentEditable === true
  );
}

/**
 * A keyup can go missing — the window loses focus mid-stride, the OS eats it,
 * a modifier swallows it — and a naive held-key set then walks you into a wall
 * forever. Browsers auto-repeat keydown while a key is physically down, so we
 * treat each keydown as a heartbeat and forget keys that stop repeating.
 */
const KEY_HEARTBEAT_MS = 900;

export class InputController {
  /** Key code -> timestamp of its most recent keydown. */
  private readonly held = new Map<string, number>();
  /**
   * Analog direction from a non-keyboard source (touch joystick, gamepad).
   * `dx`/`dy` are a unit vector; `magnitude` scales walking speed, and 0 means
   * "not being used", which lets the keyboard take over.
   */
  private analog = { dx: 0, dy: 0, magnitude: 0 };
  private lastSentAt = 0;
  private lastSent = { x: 0, y: 0, facing: 'down' as Facing, moving: false };

  constructor(
    private readonly store: Store,
    private readonly connection: Connection,
  ) {
    window.addEventListener('keydown', (event) => {
      if (isTyping()) return;
      if (KEY_TO_AXIS[event.code]) {
        this.held.set(event.code, performance.now());
        event.preventDefault();
      }
    });
    window.addEventListener('keyup', (event) => {
      this.held.delete(event.code);
    });
    // Losing focus or switching tabs mid-stride should stop you.
    window.addEventListener('blur', () => this.held.clear());
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') this.held.clear();
    });
  }

  /** Advance the local avatar by one frame. */
  update(dt: number): void {
    const me = this.store.me;
    const map = this.store.map;
    if (!me || !map) return;

    // A dialog opening mid-stride, or clicking into the chat box while walking,
    // should stop you now rather than when the key heartbeat lapses.
    if (isTyping()) {
      this.held.clear();
      this.analog = { dx: 0, dy: 0, magnitude: 0 };
    }

    // Drop keys that stopped repeating: their keyup never arrived.
    const now = performance.now();
    for (const [code, seenAt] of this.held) {
      if (now - seenAt > KEY_HEARTBEAT_MS) this.held.delete(code);
    }

    let dx = 0;
    let dy = 0;
    for (const code of this.held.keys()) {
      const axis = KEY_TO_AXIS[code];
      if (!axis) continue;
      dx += axis.dx;
      dy += axis.dy;
    }
    // Opposite keys held together cancel out.
    dx = Math.sign(dx);
    dy = Math.sign(dy);

    // Keyboard wins if both are active, so a touchscreen laptop with a keyboard
    // does not fight itself.
    let speedScale = 1;
    if (dx === 0 && dy === 0 && this.analog.magnitude > 0) {
      dx = this.analog.dx;
      dy = this.analog.dy;
      speedScale = this.analog.magnitude;
    }

    const moving = dx !== 0 || dy !== 0;
    if (moving) {
      // Normalise so diagonals are not faster.
      const length = Math.hypot(dx, dy) || 1;
      const step = WALK_SPEED * dt * speedScale;
      const moved = resolveMove(map, me.x, me.y, (dx / length) * step, (dy / length) * step);
      me.x = moved.x;
      me.y = moved.y;
      // Dominant axis decides facing; horizontal wins a tie, which reads better
      // than flickering on a perfect diagonal.
      if (Math.abs(dx) >= Math.abs(dy)) me.facing = dx > 0 ? 'right' : 'left';
      else me.facing = dy > 0 ? 'down' : 'up';
    }
    me.moving = moving;
    me.renderX = me.x;
    me.renderY = me.y;

    this.report(moving);
  }

  /** Throttle position reports, and always send the final "stopped" frame. */
  private report(moving: boolean): void {
    const me = this.store.me;
    if (!me) return;
    const now = performance.now();
    const interval = 1000 / INPUT_HZ;
    const stoppedChanged = this.lastSent.moving !== moving;
    const facingChanged = this.lastSent.facing !== me.facing;
    const drifted = Math.hypot(me.x - this.lastSent.x, me.y - this.lastSent.y) > 0.5;

    if (!stoppedChanged && !facingChanged && !drifted) return;
    if (!stoppedChanged && now - this.lastSentAt < interval) return;

    this.lastSentAt = now;
    this.lastSent = { x: me.x, y: me.y, facing: me.facing, moving };
    this.connection.send({ t: 'move', x: me.x, y: me.y, facing: me.facing, moving });
  }

  /**
   * Feed an analog direction, e.g. from a touch joystick. Pass a zero vector to
   * release it. Ignored while a movement key is held.
   */
  setAnalog(dx: number, dy: number, magnitude: number): void {
    const length = Math.hypot(dx, dy);
    this.analog =
      length > 0 && magnitude > 0
        ? { dx: dx / length, dy: dy / length, magnitude: Math.min(1, magnitude) }
        : { dx: 0, dy: 0, magnitude: 0 };
  }

  /** Apply a server correction, discarding local prediction. */
  correct(x: number, y: number): void {
    const me = this.store.me;
    if (!me) return;
    me.x = x;
    me.y = y;
    me.renderX = x;
    me.renderY = y;
    me.targetX = x;
    me.targetY = y;
    this.lastSent = { x, y, facing: me.facing, moving: me.moving };
  }

  clear(): void {
    this.held.clear();
  }
}
